import { useMainStore } from "@/stores/store";

import i18n from "@/i18n";
import { parseIntakeFormDate } from "@/util/IntakeFormDateUtil";

function parseCutoffDate(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value);
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return value < 1e12 ? new Date(value * 1000) : new Date(value);
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    const intakeFormDate = parseIntakeFormDate(trimmedValue);
    if (intakeFormDate) {
      return intakeFormDate;
    }

    if (/^\d+$/.test(trimmedValue)) {
      const numericValue = Number(trimmedValue);
      return numericValue < 1e12
        ? new Date(numericValue * 1000)
        : new Date(numericValue);
    }

    const date = new Date(trimmedValue);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

function parseCutoffTime(value = "00:00") {
  const match = String(value ?? "00:00")
    .trim()
    .match(/^([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/);

  if (!match) {
    return null;
  }

  return {
    hours: Number(match[1]),
    minutes: Number(match[2]),
  };
}

const functions = {
  // De backend levert volle slots sinds de wachtlijst gewoon mee (is_full = true).
  // Een slot is nog kiesbaar als het niet vol is, of als de wachtlijst aan staat.
  slotSelectable(slot) {
    if (!slot || slot.type === "break") return false;
    if (!(Number(slot.spots) > 0)) return false;
    return !slot.is_full || !!slot.waitlist;
  },

  // Vol + wachtlijst aan → aanmelden leidt tot een wachtlijstplek.
  slotWaitlist(slot) {
    return !!(slot && slot.is_full && slot.waitlist);
  },

  // Een vestiging is online als al haar kiesbare tijdsloten online zijn. Lees
  // dat van de tijdsloten, zoals stap 3 en 4 doen, niet van de vestiging zelf:
  // een online vestiging draagt het Flexwell-adres in data (city "Utrecht").
  locationIsOnline(location) {
    const slots = (location?.specialists ?? [])
      .flatMap((specialist) => specialist.dates ?? [])
      .flatMap((date) => date.slots ?? [])
      .filter((slot) => functions.slotSelectable(slot));
    return (
      slots.length > 0 && slots.every((slot) => slot.location_type === "online")
    );
  },

  /**
   * Zoek een tijdslot terug in de activiteitenboom op zijn id.
   *
   * Het Aanmeldingenoverzicht start de aanmeldflow vanaf een specifieke rij en
   * geeft alleen het slot-id mee in de URL. Het slot zelf komt hiervandaan, uit
   * dezelfde boom die de flow zelf ook gebruikt: een uit de overzichtsrij
   * gereconstrueerd slot mist is_full en waitlist, en dan denkt de flow dat een
   * vol tijdslot nog vrij is.
   *
   * Geeft {location, specialist, date, slot} terug, of null.
   */
  findSlotInActivity(activity, slotId) {
    const wanted = String(slotId);
    for (const location of activity?.locations ?? []) {
      for (const specialist of location.specialists ?? []) {
        for (const date of specialist.dates ?? []) {
          const slot = (date.slots ?? []).find((s) => String(s.id) === wanted);
          if (slot) return { location, specialist, date, slot };
        }
      }
    }
    return null;
  },

  inWaitlist(slot) {
    const store = useMainStore();
    if (!slot || !store.user) return false;
    return !!store.user.enrolls?.some(
      (enroll) =>
        enroll.slot_id === slot.id &&
        enroll.status === "waitlist" &&
        enroll.date === slot.date
    );
  },

  checkDate(date, start, end, type) {
    const store = useMainStore();
    var enrolls = [];
    if (type == "other_enroll" || type == "enroll_new_user") {
      enrolls = store.target_user.enrolls ?? [];
    } else {
      enrolls = store.user?.enrolls ?? [];
    }
    return enrolls.some((enroll) => this.isSameDay(enroll.date, date) && this.isTimeInRange(start, end, enroll.start, enroll.end));
  },

  getEnroll(date, start, end, language, type) {
    const store = useMainStore();
    var enrolls = [];
    if (type == "other_enroll" || type == "enroll_new_user") {
      enrolls = store.target_user.enrolls;
    } else {
      enrolls = store.user.enrolls;
    }

    const enroll = enrolls.find((enroll) => this.isSameDay(enroll.date, date) && this.isTimeInRange(start, end, enroll.start, enroll.end));

    if (!enroll) return null; // Return null if no matching enroll is found

    // Return the correct banner title based on language
    return language === "nl" ? enroll.content?.nl?.banner?.title || null : enroll.content?.eng?.banner?.title || null;
  },

  // `jsDate` komt uit de activiteitenboom, die elk scherm met
  // toAmsterdamCalendarDate omzet: een lokale middernacht, dus zijn lokale dag
  // ís de Amsterdamse. Hem nog eens in Amsterdam lezen gaf oost daarvan de dag
  // ervoor, en dan zag de wizard een bestaande aanmelding niet.
  isSameDay(phpEpoch, jsDate) {
    const dt1 = this.convertToAmsterdamDate(phpEpoch * 1000); // Convert PHP epoch (seconds) to JS timestamp (milliseconds)

    return dt1.getFullYear() === jsDate.getFullYear() && dt1.getMonth() === jsDate.getMonth() && dt1.getDate() === jsDate.getDate();
  },

  convertToAmsterdamDate(timestamp) {
    const date = new Date(timestamp);

    // Extract the correct year, month, and day using Amsterdam timezone
    const formatter = new Intl.DateTimeFormat("nl-NL", {
      timeZone: "Europe/Amsterdam",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    const formattedDate = formatter.format(date);
    const [day, month, year] = formattedDate.split("-").map(Number);

    return new Date(year, month - 1, day); // Convert back to Date object
  },

  /** YYYY-MM-DD calendar day in Europe/Amsterdam (stable across user timezones). */
  formatYmdAmsterdam(input) {
    const date =
      input instanceof Date
        ? input
        : typeof input === "number" && Number.isFinite(input)
          ? new Date(input)
          : new Date(input);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/Amsterdam",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  },

  /**
   * Period key (YYYY-MM-DD) for credits: week = Monday start, month/year = first day.
   * `dateKey` should be an Amsterdam calendar day (YYYY-MM-DD from formatYmdAmsterdam) or a Date.
   */
  getPeriodStartYmd(dateKey, period) {
    const ymd =
      typeof dateKey === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateKey)
        ? dateKey
        : this.formatYmdAmsterdam(dateKey);
    if (!ymd) {
      return "";
    }
    const [y, m, d] = ymd.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    if (period === "week") {
      const day = dt.getUTCDay();
      const daysFromMonday = day === 0 ? 6 : day - 1;
      dt.setUTCDate(dt.getUTCDate() - daysFromMonday);
    } else if (period === "month") {
      dt.setUTCDate(1);
    } else if (period === "year") {
      dt.setUTCMonth(0, 1);
    } else {
      return ymd;
    }
    const pad = (n) => String(n).padStart(2, "0");
    return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
  },

  isTimeInRange(start, end, enrollStart, enrollEnd) {
    const timeToMinutes = (time) => {
      if (!time || typeof time !== "string" || !time.includes(":")) return null;
      const [h, m] = time.split(":").map(Number);
      return h * 60 + m;
    };

    const startMin = timeToMinutes(start);
    const endMin = timeToMinutes(end);
    const enrollStartMin = timeToMinutes(enrollStart);
    const enrollEndMin = timeToMinutes(enrollEnd);

    if ([startMin, endMin, enrollStartMin, enrollEndMin].includes(null)) return false;

    const isExactMatch = startMin === enrollStartMin && endMin === enrollEndMin;
    const isStartStrictlyInside = enrollStartMin < startMin && startMin < enrollEndMin;
    const isEndStrictlyInside = enrollStartMin < endMin && endMin < enrollEndMin;

    // ✅ Return true if either strictly inside or exactly matches the enrollment
    return isExactMatch || isStartStrictlyInside || isEndStrictlyInside;
  },

  hasHtmlContent(html) {
    if (typeof html !== "string") return false;

    // Media-only content telt ook als gevuld
    if (/<(img|iframe|video|embed|hr)\b/i.test(html)) return true;

    return (
      html
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/gi, " ")
        .trim() !== ""
    );
  },

  convertCancellationPeriod(period) {
    if (period >= 1) {
      return `${period} ${i18n.global.t("time.hours")}`;
    }

    const minutes = period * 60;
    if (minutes >= 1) {
      return `${Math.round(minutes)} ${i18n.global.t("time.minutes")}`;
    }

    const seconds = minutes * 60;
    return `${Math.round(seconds)} ${i18n.global.t("time.seconds")}`;
  },
  minHoursFromNowForIntakeform(startDate, startTime = "00:00", cutoffHours = 0) {
    const dt = parseCutoffDate(startDate);
    if (!dt) return false;

    const time = parseCutoffTime(startTime);
    const h = Number(cutoffHours);

    if (!time) return false;
    if (!Number.isFinite(h) || h < 0) return true;

    dt.setHours(time.hours, time.minutes, 0, 0);

    return dt.getTime() >= Date.now() + h * 3600000;
  }
};

export default functions;
